import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  Filter,
  Printer,
  Download,
  ChevronDown,
  Calendar,
  Search,
  FileSpreadsheet
} from 'lucide-react';
import { Row, Col, Button, Card, Table, Dropdown, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { loadStatusDisplayAsStored } from '../constants/logisticsDisplay';
import * as XLSX from 'xlsx';
import './ReleaseReport.css';

const ReleaseReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, projectId } = useParams();
  
  const [project, setProject] = useState(location.state?.project || null);
  const [company, setCompany] = useState(location.state?.company || null);
  const [releases, setReleases] = useState([]);
  const [loads, setLoads] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Grouping mode: 'Release', 'BOL', 'LineItem' - default to 'Release'
  const [groupingMode, setGroupingMode] = useState('Release');
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  // Scroll to top when component mounts or projectId changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [projectId, companyId]);

  // Always merge project from API so fields like region (not on navigation state) are present
  useEffect(() => {
    if (!projectId || !companyId) return;

    const fetchProjectData = async () => {
      try {
        setError(null);

        const response = await apiService.getProjectById(projectId);

        if (response.success) {
          setProject((prev) => ({ ...(prev || {}), ...response.data }));
        } else {
          setError(response.message || 'Failed to load project details');
        }

        const companyResponse = await apiService.getCompanyById(companyId);
        if (companyResponse.success) {
          setCompany((prev) => prev || companyResponse.data);
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
        setError('Failed to load project details');
      }
    };

    fetchProjectData();
  }, [projectId, companyId]);

  // Fetch releases and load data (depend on id only so merging project fields like region does not refetch)
  useEffect(() => {
    if (project?.id) {
      fetchReportData();
    }
  }, [project?.id]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch releases
      const releasesResponse = await apiService.getProjectReleases(project.id);
      
      if (releasesResponse.success) {
        setReleases(releasesResponse.data || []);
      }

      // Fetch loads to get BOL numbers
      const loadsResponse = await apiService.getProjectLoads(project.id);
      
      if (loadsResponse.success) {
        setLoads(loadsResponse.data || []);
      }

    } catch (error) {
      console.error('Error fetching report data:', error);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  // Process release data for display
  const processReleaseData = (release) => {
    const load = loads.find(l => l.id === release.loadId);
    const inTransitDate = release.fabricatedDate || release.taggedDate || release.firmedDate;
    const projectRegion =
      (project?.region ?? project?.Region ?? '').toString().trim() || '';

    return {
      id: release.id,
      release: release.releaseNumber || release.release || '',
      description: release.description || '',
      status: release.status || 'Unknown',
      cli: release.lineItem || release.bidItemNumber || '',
      sac: release.sacCode || '',
      acct: release.acctCode || '',
      region: projectRegion || '--',
      shipWeight: release.weight ? (typeof release.weight === 'number' ? release.weight.toFixed(0) : release.weight) : '--',
      inTransit: inTransitDate ? formatDate(inTransitDate) : '--',
      placedWeight:
        release.status?.toLowerCase() === 'placed'
          ? (release.placedWt
              ? (typeof release.placedWt === 'number' ? release.placedWt.toFixed(0) : release.placedWt)
              : '')
          : '',
      placed: release.status?.toLowerCase() === 'placed' && release.placedDate ? formatDate(release.placedDate) : '',
      lineItem: release.lineItem || release.bidItemNumber || null,
      bolNumber: load?.bolNo || null,
      loadId: release.loadId || null,
      shipVia: load?.shipVia || null,
      trailerNo: load?.trailerNo || null
    };
  };

  // Process data by Release (flat list)
  const processByRelease = () => {
    if (!releases || !Array.isArray(releases) || releases.length === 0) return [];
    
    return releases
      .map(processReleaseData)
      .sort((a, b) => {
        // Sort by release number
        const aNum = parseInt(a.release.replace(/\D/g, '')) || 0;
        const bNum = parseInt(b.release.replace(/\D/g, '')) || 0;
        if (aNum !== bNum) return aNum - bNum;
        return a.release.localeCompare(b.release);
      });
  };

  // Process data by BOL
  const processByBOL = () => {
    if (!releases || !Array.isArray(releases) || releases.length === 0) return [];
    if (!loads || !Array.isArray(loads) || loads.length === 0) return [];

    // Create a map of loadId to load data
    const loadMap = {};
    loads.forEach(load => {
      if (load && load.id) {
        loadMap[load.id] = {
          bolNo: load.bolNo || `BOL ${load.id}`,
          shipVia: load.shipVia || 'NA',
          trailerNo: load.trailerNo || 'NA'
        };
      }
    });

    // Group releases by BOL
    const bolGroups = {};
    releases.forEach(release => {
      if (release) {
        const load = loadMap[release.loadId];
        const bolNumber = load?.bolNo || 'Unknown BOL';
        if (!bolGroups[bolNumber]) {
          bolGroups[bolNumber] = {
            bolNumber,
            shipVia: load?.shipVia || 'NA',
            trailerNo: load?.trailerNo || 'NA',
            releases: []
          };
        }
        bolGroups[bolNumber].releases.push(processReleaseData(release));
      }
    });

    // Convert to array and sort
    return Object.values(bolGroups)
      .sort((a, b) => a.bolNumber.localeCompare(b.bolNumber))
      .map(group => ({
        ...group,
        releases: (group.releases || []).sort((a, b) => {
          const aNum = parseInt(a.release.replace(/\D/g, '')) || 0;
          const bNum = parseInt(b.release.replace(/\D/g, '')) || 0;
          if (aNum !== bNum) return aNum - bNum;
          return a.release.localeCompare(b.release);
        })
      }));
  };

  // Process data by Line Item
  const processByLineItem = () => {
    if (!releases || !Array.isArray(releases) || releases.length === 0) return [];

    // Group releases by Line Item
    const lineItemGroups = {};
    releases.forEach(release => {
      if (release) {
        const processed = processReleaseData(release);
        const lineItemKeyRaw = (processed.lineItem ?? '').toString().normalize('NFKC');
        const lineItem =
          lineItemKeyRaw.replace(/[\s\u00A0]+/g, '').trim() || 'Unknown';
        
        if (!lineItemGroups[lineItem]) {
          lineItemGroups[lineItem] = {
            lineItem,
            releases: []
          };
        }
        lineItemGroups[lineItem].releases.push(processed);
      }
    });

    // Convert to array and sort
    return Object.values(lineItemGroups)
      .sort((a, b) => {
        // Sort line items numerically if possible
        const aNum = parseInt(a.lineItem) || 0;
        const bNum = parseInt(b.lineItem) || 0;
        if (aNum !== bNum) return aNum - bNum;
        return a.lineItem.localeCompare(b.lineItem);
      })
      .map(group => ({
        ...group,
        releases: (group.releases || []).sort((a, b) => {
          const aNum = parseInt(a.release.replace(/\D/g, '')) || 0;
          const bNum = parseInt(b.release.replace(/\D/g, '')) || 0;
          if (aNum !== bNum) return aNum - bNum;
          return a.release.localeCompare(b.release);
        }),
        // Calculate totals
        totalShipWeight: (group.releases || []).reduce((sum, r) => {
          const weight = parseFloat(r.shipWeight) || 0;
          return sum + weight;
        }, 0),
        totalPlacedWeight: (group.releases || []).reduce((sum, r) => {
          const weight = parseFloat(r.placedWeight) || 0;
          return sum + weight;
        }, 0)
      }));
  };

  // Apply filters based on grouping mode
  useEffect(() => {
    let rawData = [];
    
    // Get raw data based on grouping mode
    if (groupingMode === 'Release') {
      rawData = processByRelease();
    } else if (groupingMode === 'BOL') {
      rawData = processByBOL();
    } else if (groupingMode === 'LineItem') {
      rawData = processByLineItem();
    }

    // Ensure rawData is always an array
    if (!Array.isArray(rawData)) {
      rawData = [];
    }

    // Apply filters
    let filtered = rawData;

    if (groupingMode === 'Release') {
      // Filter flat list
      if (statusFilter !== 'All' && Array.isArray(filtered)) {
        filtered = filtered.filter(release => 
          release && release.status && release.status.toLowerCase() === statusFilter.toLowerCase()
        );
      }

      if ((startDate || endDate) && Array.isArray(filtered)) {
        filtered = filtered.filter(release => {
          if (!release || !release.inTransit || release.inTransit === '--') return true;
          try {
            const releaseDate = new Date(release.inTransit);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;
            
            if (start && releaseDate < start) return false;
            if (end && releaseDate > end) return false;
            
            return true;
          } catch {
            return true;
          }
        });
      }
    } else {
      // Filter grouped data (BOL or LineItem)
      if (statusFilter !== 'All' && Array.isArray(filtered)) {
        filtered = filtered.map(group => {
          if (!group || !group.releases || !Array.isArray(group.releases)) return null;
          return {
            ...group,
            releases: group.releases.filter(release => 
              release && release.status && release.status.toLowerCase() === statusFilter.toLowerCase()
            )
          };
        }).filter(group => group && group.releases && group.releases.length > 0);
      }

      if ((startDate || endDate) && Array.isArray(filtered)) {
        filtered = filtered.map(group => {
          if (!group || !group.releases || !Array.isArray(group.releases)) return null;
          return {
            ...group,
            releases: group.releases.filter(release => {
              if (!release || !release.inTransit || release.inTransit === '--') return true;
              try {
                const releaseDate = new Date(release.inTransit);
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;
                
                if (start && releaseDate < start) return false;
                if (end && releaseDate > end) return false;
                
                return true;
              } catch {
                return true;
              }
            })
          };
        }).filter(group => group && group.releases && group.releases.length > 0);
      }
    }

    setFilteredData(Array.isArray(filtered) ? filtered : []);
  }, [releases, loads, project, groupingMode, statusFilter, startDate, endDate]);

  // Get job number and name (customer first, then Tyler)
  const getJobNumber = () => {
    return project?.custProjNo || project?.tylerNumber || project?.projectNumber || '--';
  };

  const getJobName = () => {
    return project?.custProjName || project?.tylerProjectName || project?.projectName || '--';
  };

  // Get unique statuses from releases data
  const getUniqueStatuses = () => {
    const statusSet = new Set();
    
    if (!filteredData || !Array.isArray(filteredData) || filteredData.length === 0) {
      return [];
    }
    
    if (groupingMode === 'Release') {
      filteredData.forEach(release => {
        if (release && release.status && release.status !== 'Unknown') {
          statusSet.add(release.status);
        }
      });
    } else {
      filteredData.forEach(group => {
        if (group && group.releases && Array.isArray(group.releases)) {
          group.releases.forEach(release => {
            if (release && release.status && release.status !== 'Unknown') {
              statusSet.add(release.status);
            }
          });
        }
      });
    }
    
    return Array.from(statusSet).sort();
  };

  // Format statuses into two lines
  const formatStatusesForDisplay = () => {
    const statuses = getUniqueStatuses();
    if (statuses.length === 0) return 'None';
    
    return statuses.map((st) => loadStatusDisplayAsStored(st)).join(', ');
  };

  const handlePrint = () => {
    // Get the report content
    const reportContent = document.getElementById('release-report-content');
    if (!reportContent) {
      console.error('Report content not found');
      return;
    }
    
    // Build header HTML
    const headerHTML = `
      <div class="print-header">
        <div class="print-header-left">
          <img src="/Logo.jpg" alt="Tyler Reinforcing" class="print-logo" />
          <div class="print-company-info">
            <div class="print-company-name">Tyler Reinforcing</div>
            <div class="print-company-phone">(888) 70-REBAR</div>
          </div>
          <div class="print-job-info">
            <div class="print-job-row">
              <span class="print-label">Job Number:</span>
              <span class="print-value">${getJobNumber()}</span>
            </div>
            <div class="print-job-row">
              <span class="print-label">Job Name:</span>
              <span class="print-value">${getJobName()}</span>
            </div>
            <div class="print-job-row">
              <span class="print-label">Customer:</span>
              <span class="print-value">${company?.name || '--'}</span>
            </div>
          </div>
        </div>
        <div class="print-header-right">
          <div class="print-report-title">Release Report</div>
          <div class="print-report-meta">
            <div class="print-meta-line">
              <span class="print-meta-label">Dates:</span>
              <span class="print-meta-value">${startDate || endDate ? (startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : startDate ? `From ${formatDate(startDate)}` : `Until ${formatDate(endDate)}`) : 'All'}</span>
            </div>
            <div class="print-meta-line">
              <span class="print-meta-label">Status:</span>
              <span class="print-meta-value">${formatStatusesForDisplay()}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const currentTime = new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Release Report - ${getJobName()}</title>
          <meta charset="utf-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 11px;
              color: #333;
              margin: 20px 25px;
              background: white;
            }
            
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 1px solid #ccc;
            }
            
            .print-header-left {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 12px;
            }
            
            .print-logo {
              max-width: 200px;
              max-height: 100px;
              height: auto;
              object-fit: contain;
            }
            
            .print-company-info {
              margin-top: 4px;
            }
            
            .print-company-name {
              font-size: 12px;
              font-weight: bold;
              color: #333;
              margin-bottom: 2px;
            }
            
            .print-company-phone {
              font-size: 10px;
              color: #666;
            }
            
            .print-job-info {
              display: flex;
              flex-direction: column;
              gap: 4px;
              margin-top: 8px;
            }
            
            .print-job-row {
              display: flex;
              gap: 8px;
              font-size: 11px;
              line-height: 1.4;
            }
            
            .print-label {
              font-weight: bold;
              color: #666;
              min-width: 80px;
            }
            
            .print-value {
              color: #000;
            }
            
            .print-header-right {
              flex: 1;
              text-align: right;
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 8px;
            }
            
            .print-report-title {
              font-size: 22px;
              font-weight: bold;
              color: #000;
              margin-bottom: 4px;
            }
            
            .print-report-meta {
              display: flex;
              flex-direction: column;
              gap: 4px;
              font-size: 11px;
            }
            
            .print-meta-line {
              display: flex;
              gap: 6px;
              align-items: baseline;
            }
            
            .print-meta-label {
              font-weight: bold;
              color: #666;
            }
            
            .print-meta-value {
              color: #000;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 11px;
              border: none !important;
            }
            
            table * {
              border-collapse: collapse !important;
            }
            
            thead,
            tbody,
            tr,
            th,
            td {
              border: none !important;
              border-top: none !important;
              border-bottom: none !important;
              border-left: none !important;
              border-right: none !important;
            }
            
            thead th {
              background-color: #e6e6e6 !important;
              font-weight: bold;
              padding: 6px 8px;
              text-align: center;
              border: none !important;
              font-size: 12px;
            }
            
            thead th:nth-child(1),
            thead th:nth-child(2) {
              text-align: left;
            }
            
            tbody td {
              padding: 6px 8px;
              border: none !important;
              font-size: 11px;
            }
            
            tbody td:nth-child(1),
            tbody td:nth-child(2) {
              text-align: left;
            }
            
            tbody td:nth-child(3),
            tbody td:nth-child(4),
            tbody td:nth-child(5),
            tbody td:nth-child(6),
            tbody td:nth-child(7) {
              text-align: center;
            }
            
            tbody td:nth-child(8),
            tbody td:nth-child(10) {
              text-align: right;
            }
            
            tbody td:nth-child(9),
            tbody td:nth-child(11) {
              text-align: center;
            }
            
            .section-header,
            .bol-header,
            .line-item-header {
              background-color: #d9d9d9 !important;
              font-weight: bold;
              padding: 6px 8px;
              margin-top: 20px;
              border: none !important;
              font-size: 14px;
            }
            
            .section-header td,
            .bol-header td,
            .line-item-header td {
              background-color: #d9d9d9 !important;
              border: none !important;
              color: #000 !important;
            }
            
            .row-even {
              background-color: white;
            }
            
            .row-odd {
              background-color: #e6e6e6;
            }
            
            tbody tr.row-even td {
              background-color: white !important;
            }
            
            tbody tr.row-odd td {
              background-color: #e6e6e6 !important;
            }
            
            .section-total,
            .line-item-total {
              background-color: white !important;
              font-weight: bold;
              border: none !important;
            }
            
            .section-total td,
            .line-item-total td {
              background-color: white !important;
              border: none !important;
              padding: 6px 8px;
            }
            
            .section-total td:first-child,
            .line-item-total td:first-child {
              text-align: right;
            }
            
            .print-footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #666;
            }
            
            .print-footer-left {
              flex: 1;
            }
            
            .print-footer-center {
              flex: 1;
              text-align: center;
            }
            
            .print-footer-right {
              flex: 1;
              text-align: right;
            }
            
            @media print {
              @page {
                margin: 20px 25px;
                size: letter;
              }
              
              body {
                margin: 0;
              }
              
              .print-header {
                page-break-inside: avoid;
              }
              
              .section-header {
                page-break-after: avoid;
              }
              
              tbody tr {
                page-break-inside: avoid;
              }
              
              thead {
                display: table-header-group;
              }
              
              tfoot {
                display: table-footer-group;
              }
            }
          </style>
        </head>
        <body>
          ${headerHTML}
          ${reportContent.innerHTML}
          <div class="print-footer">
            <div class="print-footer-left">${currentDate} ${currentTime}</div>
            <div class="print-footer-center">© ${new Date().getFullYear()} Tyler Reinforcing LLC</div>
            <div class="print-footer-right">Page <span class="page-number"></span></div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Add page numbers after content loads
    setTimeout(() => {
      const pageNumbers = printWindow.document.querySelectorAll('.page-number');
      // Simple page number - in a real implementation, you'd calculate based on content
      pageNumbers.forEach((el, index) => {
        el.textContent = '1';
      });
    }, 100);
    
    printWindow.print();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('All');
  };

  const handleExportPDF = () => {
    import('jspdf').then(({ jsPDF }) => {
      import('jspdf-autotable').then(({ default: autoTable }) => {
        const doc = new jsPDF('landscape');
        
        // Function to add header (logo, job info, report info)
        const addHeader = (doc, img = null) => {
          const leftMargin = 14;
          let logoHeight = 0;
          
          // Add logo on the left if available
          if (img) {
            const logoWidth = 32; // Reduced size for PDF
            logoHeight = (img.height / img.width) * logoWidth;
            doc.addImage(img, 'JPEG', leftMargin, 8, logoWidth, logoHeight);
            
            // Add tagline and phone below logo
            doc.setFontSize(6);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(108, 117, 125);
            const taglineY = 8 + logoHeight + 3;
            doc.text('\u201CWe Reinforce Confidence\u201D', leftMargin, taglineY);
            doc.text('(888) 70-REBAR', leftMargin, taglineY + 4);
          }
          
          // Job info next to logo (left side)
          let yPos = 10;
          const jobInfoX = img ? (leftMargin + 32 + 10) : leftMargin; // Position after logo with gap, or start at left if no logo
          doc.setFontSize(7);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(108, 117, 125);
          doc.text('Job Number:', jobInfoX, yPos);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          doc.text(getJobNumber(), jobInfoX + 25, yPos);
          
          doc.setFont(undefined, 'bold');
          doc.setTextColor(108, 117, 125);
          doc.text('Job Name:', jobInfoX, yPos + 4);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          const jobName = getJobName();
          const maxJobNameWidth = 80;
          const jobNameText = doc.splitTextToSize(jobName, maxJobNameWidth);
          doc.text(jobNameText, jobInfoX + 25, yPos + 4);
          
          doc.setFont(undefined, 'bold');
          doc.setTextColor(108, 117, 125);
          doc.text('Customer:', jobInfoX, yPos + 8);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          const customerName = company?.name || '--';
          const customerText = doc.splitTextToSize(customerName, maxJobNameWidth);
          doc.text(customerText, jobInfoX + 25, yPos + 8);
          
          // Report title, dates, and status on the right side
          const pageWidth = doc.internal.pageSize.getWidth();
          const rightMargin = 14;
          
          // Report title
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 0, 0);
          const titleWidth = doc.getTextWidth('Release Report');
          doc.text('Release Report', pageWidth - rightMargin - titleWidth, 15);
          
          // Dates and status (right-aligned, on separate lines)
          yPos = 20;
          doc.setFontSize(7);
          
          // Dates line
          const dateLabel = 'Dates:';
          const dateRange = startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : 
                           startDate ? `From ${formatDate(startDate)}` :
                           endDate ? `Until ${formatDate(endDate)}` : 'All';
          doc.setFont(undefined, 'bold');
          doc.setTextColor(108, 117, 125);
          const dateLabelWidth = doc.getTextWidth(dateLabel);
          const dateRangeWidth = doc.getTextWidth(dateRange);
          const dateTotalWidth = dateLabelWidth + 3 + dateRangeWidth; // 3 units spacing
          doc.text(dateLabel, pageWidth - rightMargin - dateTotalWidth, yPos);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          doc.text(dateRange, pageWidth - rightMargin - dateRangeWidth, yPos);
          
          // Status line (on next line)
          yPos = yPos + 4;
          const statusLabel = 'Status:';
          const statusText = formatStatusesForDisplay();
          doc.setFont(undefined, 'bold');
          doc.setTextColor(108, 117, 125);
          const statusLabelWidth = doc.getTextWidth(statusLabel);
          const statusTextWidth = doc.getTextWidth(statusText);
          const statusTotalWidth = statusLabelWidth + 3 + statusTextWidth; // 3 units spacing
          doc.text(statusLabel, pageWidth - rightMargin - statusTotalWidth, yPos);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          doc.text(statusText, pageWidth - rightMargin - statusTextWidth, yPos);
        };
        
        // Function to generate the table
        const generatePDFTable = (doc, autoTable) => {
          // Prepare table data
          const tableData = [];
          const headers = groupingMode === 'Release' 
            ? ['Release', 'Description', 'Status', 'CLI', 'SAC', 'Acct', 'Loc', 'Shp-Wt', 'Shipped', 'Pla-Wt', 'Placed']
            : ['Release', 'Description', 'Status', 'CLI', 'SAC', 'Acct', 'Loc', 'Shp-Wt', 'Shipped', 'Pla-Wt', 'Placed'];
          
          if (groupingMode === 'Release') {
            filteredData.forEach((release, index) => {
              const rowColor = index % 2 === 0 ? [255, 255, 255] : [230, 230, 230]; // White or gray
              tableData.push([
                { content: release.release || '--', styles: { fillColor: rowColor } },
                { content: release.description || '--', styles: { fillColor: rowColor } },
                {
                  content: release.status ? loadStatusDisplayAsStored(release.status) : '--',
                  styles: { fillColor: rowColor, halign: 'center' }
                },
                { content: release.cli || '--', styles: { fillColor: rowColor } },
                { content: release.sac || '--', styles: { fillColor: rowColor } },
                { content: release.acct || '--', styles: { fillColor: rowColor } },
                { content: release.region || '--', styles: { fillColor: rowColor } },
                { content: release.shipWeight || '--', styles: { fillColor: rowColor, halign: 'right' } },
                { content: release.inTransit || '--', styles: { fillColor: rowColor, halign: 'center' } },
                { content: release.placedWeight || '', styles: { fillColor: rowColor, halign: 'right' } },
                { content: release.placed || '--', styles: { fillColor: rowColor, halign: 'center' } }
              ]);
            });
          } else if (groupingMode === 'BOL') {
            filteredData.forEach(group => {
              // BOL header
              tableData.push([
                { content: `BOL#: ${group.bolNumber}`, colSpan: 4, styles: { fillColor: [217, 217, 217], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' } },
                { content: `Ship via: ${group.shipVia}`, colSpan: 4, styles: { fillColor: [217, 217, 217], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' } },
                { content: `Trailer: ${group.trailerNo}`, colSpan: 3, styles: { fillColor: [217, 217, 217], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right' } }
              ]);
              group.releases.forEach((release, releaseIndex) => {
                const rowColor = releaseIndex % 2 === 0 ? [255, 255, 255] : [230, 230, 230]; // White or gray
                tableData.push([
                  { content: release.release || '--', styles: { fillColor: rowColor } },
                  { content: release.description || '--', styles: { fillColor: rowColor } },
                  {
                  content: release.status ? loadStatusDisplayAsStored(release.status) : '--',
                  styles: { fillColor: rowColor, halign: 'center' }
                },
                  { content: release.cli || '--', styles: { fillColor: rowColor } },
                  { content: release.sac || '--', styles: { fillColor: rowColor } },
                  { content: release.acct || '--', styles: { fillColor: rowColor } },
                  { content: release.region || '--', styles: { fillColor: rowColor } },
                  { content: release.shipWeight || '--', styles: { fillColor: rowColor, halign: 'right' } },
                  { content: release.inTransit || '--', styles: { fillColor: rowColor, halign: 'center' } },
                  { content: release.placedWeight || '', styles: { fillColor: rowColor, halign: 'right' } },
                  { content: release.placed || '--', styles: { fillColor: rowColor, halign: 'center' } }
                ]);
              });
            });
          } else if (groupingMode === 'LineItem') {
            filteredData
              .filter((group) => group?.releases && Array.isArray(group.releases) && group.releases.length > 0)
              .forEach(group => {
              // Line Item header
              tableData.push([
                { content: `Line Item: ${group.lineItem}`, colSpan: 11, styles: { fillColor: [217, 217, 217], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' } }
              ]);
              group.releases.forEach((release, releaseIndex) => {
                const rowColor = releaseIndex % 2 === 0 ? [255, 255, 255] : [230, 230, 230]; // White or gray
                tableData.push([
                  { content: release.release || '--', styles: { fillColor: rowColor } },
                  { content: release.description || '--', styles: { fillColor: rowColor } },
                  {
                  content: release.status ? loadStatusDisplayAsStored(release.status) : '--',
                  styles: { fillColor: rowColor, halign: 'center' }
                },
                  { content: release.cli || '--', styles: { fillColor: rowColor } },
                  { content: release.sac || '--', styles: { fillColor: rowColor } },
                  { content: release.acct || '--', styles: { fillColor: rowColor } },
                  { content: release.region || '--', styles: { fillColor: rowColor } },
                  { content: release.shipWeight || '--', styles: { fillColor: rowColor, halign: 'right' } },
                  { content: release.inTransit || '--', styles: { fillColor: rowColor, halign: 'center' } },
                  { content: release.placedWeight || '', styles: { fillColor: rowColor, halign: 'right' } },
                  { content: release.placed || '--', styles: { fillColor: rowColor, halign: 'center' } }
                ]);
              });
              // Total row
              tableData.push([
                { content: '', colSpan: 6, styles: { fillColor: [255, 255, 255] } },
                { content: `Total for Line Item #${group.lineItem}:`, styles: { fontStyle: 'bold', fillColor: [255, 255, 255], halign: 'left', lineColor: [0, 0, 0], lineWidth: { top: 0.3, right: 0, bottom: 0, left: 0 } } },
                { content: group.totalShipWeight.toFixed(0), styles: { fontStyle: 'bold', halign: 'right', fillColor: [255, 255, 255], lineColor: [0, 0, 0], lineWidth: { top: 0.3, right: 0, bottom: 0, left: 0 } } },
                { content: '', styles: { fillColor: [255, 255, 255], lineColor: [0, 0, 0], lineWidth: { top: 0.3, right: 0, bottom: 0, left: 0 } } },
                { content: group.totalPlacedWeight.toFixed(0), styles: { fontStyle: 'bold', halign: 'right', fillColor: [255, 255, 255], lineColor: [0, 0, 0], lineWidth: { top: 0.3, right: 0, bottom: 0, left: 0 } } },
                { content: '', styles: { fillColor: [255, 255, 255], lineColor: [0, 0, 0], lineWidth: { top: 0.3, right: 0, bottom: 0, left: 0 } } }
              ]);
            });
          }
          
          autoTable(doc, {
            startY: 35,
            head: [headers],
            body: tableData,
            theme: 'plain', // Changed from 'grid' to remove borders
            headStyles: {
              fillColor: [230, 230, 230], // #e6e6e6
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              fontSize: 8,
              lineWidth: 0, // No borders
              lineColor: [255, 255, 255] // Transparent borders
            },
            styles: {
              fontSize: 8,
              cellPadding: 2,
              lineColor: [255, 255, 255], // Transparent - no borders
              lineWidth: 0, // No borders
              textColor: [0, 0, 0]
            },
            didParseCell: function(data) {
              // Remove all borders
              data.cell.styles.lineWidth = 0;
              data.cell.styles.lineColor = [255, 255, 255];
              
              // If the cell already has a fillColor set (from tableData), keep it
              // Otherwise, handle section headers
              if (data.row.raw && data.row.raw[0] && typeof data.row.raw[0] === 'object' && data.row.raw[0].colSpan) {
                const content = data.row.raw[0].content || '';
                if (content.includes('BOL#:') || content.includes('Line Item:')) {
                  data.cell.styles.fillColor = [217, 217, 217]; // #d9d9d9 gray
                  data.cell.styles.textColor = [0, 0, 0]; // Black text
                  data.cell.styles.fontStyle = 'bold';
                } else if (content.includes('Total for Line Item')) {
                  data.cell.styles.fillColor = [255, 255, 255];
                  data.cell.styles.textColor = [0, 0, 0];
                  data.cell.styles.fontStyle = 'bold';
                }
              }

              // Match screen: draw a black top line from Loc through Placed on line-item totals
              if (data.row.raw && Array.isArray(data.row.raw) && data.row.raw[1] && typeof data.row.raw[1] === 'object') {
                const totalLabel = data.row.raw[1].content || '';
                if (String(totalLabel).includes('Total for Line Item') && data.column.index >= 6) {
                  data.cell.styles.lineColor = [0, 0, 0];
                  data.cell.styles.lineWidth = { top: 0.3, right: 0, bottom: 0, left: 0 };
                }
              }
            }
          });
          
          doc.save(`Release-Report-${getJobName()}-${new Date().toISOString().split('T')[0]}.pdf`);
        };
        
        // Load and add logo image from public folder
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Allow CORS if needed
        // Use the logo from public folder - try multiple paths for compatibility
        const publicUrl = process.env.PUBLIC_URL || '';
        img.src = publicUrl + '/Logo.jpg';
        
        img.onload = () => {
          addHeader(doc, img);
          generatePDFTable(doc, autoTable);
        };
        
        img.onerror = () => {
          // Try alternative path if first fails
          const img2 = new Image();
          img2.crossOrigin = 'anonymous';
          img2.src = '/Logo.jpg';
          
          img2.onload = () => {
            addHeader(doc, img2);
            generatePDFTable(doc, autoTable);
          };
          
          img2.onerror = () => {
            // If image fails to load, continue without logo
            console.warn('Logo image failed to load, continuing without logo');
            addHeader(doc);
            generatePDFTable(doc, autoTable);
          };
        };
      });
    });
  };

  const handleExportExcel = () => {
    const excelData = [];
    
    // Add header row
    excelData.push([
      'Release',
      'Description', 
      'Status',
      'CLI',
      'SAC',
      'Acct',
      'Loc',
      'Shp-Wt',
      'Shipped',
      'Pla-Wt',
      'Placed',
      groupingMode === 'BOL' ? 'BOL Number' : groupingMode === 'LineItem' ? 'Line Item' : ''
    ]);
    
    if (groupingMode === 'Release') {
      filteredData.forEach(release => {
        excelData.push([
          release.release || 'N/A',
          release.description || 'N/A',
          release.status ? loadStatusDisplayAsStored(release.status) : 'Unknown',
          release.cli || 'N/A',
          release.sac || 'N/A',
          release.acct || 'N/A',
          release.region || 'N/A',
          release.shipWeight || 'N/A',
          release.inTransit || 'N/A',
          release.placedWeight ?? '',
          release.placed || 'N/A',
          ''
        ]);
      });
    } else if (groupingMode === 'BOL') {
      filteredData.forEach(group => {
        group.releases.forEach(release => {
          excelData.push([
            release.release || 'N/A',
            release.description || 'N/A',
            release.status ? loadStatusDisplayAsStored(release.status) : 'Unknown',
            release.cli || 'N/A',
            release.sac || 'N/A',
            release.acct || 'N/A',
            release.region || 'N/A',
            release.shipWeight || 'N/A',
            release.inTransit || 'N/A',
            release.placedWeight ?? '',
            release.placed || 'N/A',
            group.bolNumber || 'N/A'
          ]);
        });
      });
    } else if (groupingMode === 'LineItem') {
      filteredData.forEach(group => {
        group.releases.forEach(release => {
          excelData.push([
            release.release || 'N/A',
            release.description || 'N/A',
            release.status ? loadStatusDisplayAsStored(release.status) : 'Unknown',
            release.cli || 'N/A',
            release.sac || 'N/A',
            release.acct || 'N/A',
            release.region || 'N/A',
            release.shipWeight || 'N/A',
            release.inTransit || 'N/A',
            release.placedWeight ?? '',
            release.placed || 'N/A',
            group.lineItem || 'N/A'
          ]);
        });
      });
    }
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    const colWidths = [
      { wch: 12 }, { wch: 30 }, { wch: 14 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
      { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Release Report');
    const fileName = `Release_Report_${getJobNumber()}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (loading) {
    return (
      <div className="release-report-wrapper">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading release report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="release-report-wrapper">
        <div className="text-center py-5">
          <Alert variant="danger">
            <h4>Error Loading Report</h4>
            <p>{error}</p>
            <Button 
              variant="primary" 
              onClick={() => navigate(`/company/${companyId}/project/${projectId}`)}
            >
              Back to Project
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="release-report-wrapper">
      {/* Header */}
      <div className="report-header">
        <div className="report-header-top">
          <button 
            className="back-button"
            onClick={() => navigate(`/company/${companyId}/project/${projectId}`, {
              state: { 
                project: project || location.state?.project,
                company: company || location.state?.company,
                activeTab: 'releases'
              }
            })}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="report-title">Release Report</h1>
        </div>
        
        <div className="report-header-actions">
          {/* Grouping Mode Selector */}
          <Dropdown className="me-2">
            <Dropdown.Toggle variant="outline-primary">
              Group By: {groupingMode === 'Release' ? 'Release' : groupingMode === 'BOL' ? 'BOL' : 'Line Item'}
              <ChevronDown size={16} className="ms-2" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setGroupingMode('Release')}>By Release</Dropdown.Item>
              <Dropdown.Item onClick={() => setGroupingMode('BOL')}>By BOL</Dropdown.Item>
              <Dropdown.Item onClick={() => setGroupingMode('LineItem')}>By Line Item</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown show={showFilters} onToggle={setShowFilters}>
            <Dropdown.Toggle variant="primary" className="filter-button">
              <Filter size={16} className="me-2" />
              Filter
            </Dropdown.Toggle>
            <Dropdown.Menu className="filter-dropdown-custom">
              <div className="filter-form-container m-3">
                <div className="filter-row">
                  <div className="filter-field">
                    <label className="filter-label">Start Date</label>
                    <div className="date-input-container">
                      <input
                        type="date"
                        className="date-input"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="filter-field">
                    <label className="filter-label">End Date</label>
                    <div className="date-input-container">
                      <input
                        type="date"
                        className="date-input"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="filter-row">
                  <div className="filter-field">
                    <label className="filter-label">Status(es)</label>
                    <div className="dropdown-container">
                       <Dropdown>
                         <Dropdown.Toggle variant="light" className="status-dropdown">
                           {statusFilter === 'All'
                             ? 'All Statuses'
                             : loadStatusDisplayAsStored(statusFilter)}
                         </Dropdown.Toggle>
                        <Dropdown.Menu className="status-dropdown-menu">
                          <Dropdown.Item onClick={() => setStatusFilter('All')}>All Statuses</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Reserved')}>Reserved</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Firmed')}>Firmed</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Tagged')}>Tagged</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Fabricated')}>Fabricated</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Certified')}>Certified</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('Placed')}>Placed</Dropdown.Item>
                          <Dropdown.Item onClick={() => setStatusFilter('In Transit')}>
                            {loadStatusDisplayAsStored('In Transit')}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
                
                <div className="filter-row">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={handleClearFilters}
                    className="clear-filters-btn"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </Dropdown.Menu>
          </Dropdown>
          
          {/* <Button variant="primary" onClick={handlePrint} className="print-button">
            <Printer size={16} className="me-2" />
            Print
          </Button> */}
        </div>
      </div>

      {/* Report Header with Logo and Info */}
      <div className="report-header-compact">
        <div className="header-logo-section">
          <div className="logo-and-info-wrapper">
            <img 
              src="/Logo.jpg" 
              alt="Tyler Reinforcing" 
              className="report-logo"
              style={{ maxWidth: '240px', maxHeight: '120px', width: '240px', height: 'auto', objectFit: 'contain' }}
            />
            <div className="company-info">
              <div className="company-tagline">{'\u201CWe Reinforce Confidence\u201D'}</div>
              <div className="company-phone">(888) 70-REBAR</div>
            </div>
          </div>
          <div className="header-job-info">
            <div className="job-info-row">
              <span className="job-label">Job Number:</span>
              <span className="job-value">{getJobNumber()}</span>
            </div>
            <div className="job-info-row">
              <span className="job-label">Job Name:</span>
              <span className="job-value">{getJobName()}</span>
            </div>
            <div className="job-info-row">
              <span className="job-label">Customer:</span>
              <span className="job-value">{company?.name || '--'}</span>
            </div>
          </div>
        </div>
        <div className="header-report-info">
          <h4 className="report-title-large fw-bold">Release Report</h4>
          <div className="report-meta">
            <div className="dates-line">Dates: {startDate || endDate ? (startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : startDate ? `From ${formatDate(startDate)}` : `Until ${formatDate(endDate)}`) : 'ALL'}</div>
            <div className="status-line">
              Status: {formatStatusesForDisplay()}
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <Card className="report-card">
        <Card.Body>
          {!filteredData || !Array.isArray(filteredData) || filteredData.length === 0 ? (
            <div className="text-center py-5">
              <p>No releases found matching the current filters.</p>
            </div>
          ) : (
            <div id="release-report-content">
              <Table className="release-table" responsive>
                <thead>
                  <tr>
                    <th>Release</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>CLI</th>
                    <th>SAC</th>
                    <th>Acct</th>
                    <th>Loc</th>
                    <th>Shp-Wt</th>
                    <th>Shipped</th>
                    <th>Pla-Wt</th>
                    <th>Placed</th>
                  </tr>
                </thead>
                <tbody>
                  {groupingMode === 'Release' ? (
                    // Flat list by Release
                    filteredData.map((release, index) => (
                      <tr key={release.id || index} className={`release-row ${index % 2 === 0 ? 'row-even' : 'row-odd'}`}>
                        <td>{release.release}</td>
                        <td>{release.description}</td>
                        <td className="text-center">
                          {release.status ? loadStatusDisplayAsStored(release.status) : '—'}
                        </td>
                        <td className="text-center">{release.cli}</td>
                        <td className="text-center">{release.sac}</td>
                        <td className="text-center">{release.acct}</td>
                        <td className="text-center">{release.region}</td>
                        <td className="text-right">{release.shipWeight}</td>
                        <td className="text-right">{release.inTransit}</td>
                        <td className="text-right">{release.placedWeight}</td>
                        <td className="text-right">{release.placed}</td>
                      </tr>
                    ))
                  ) : groupingMode === 'BOL' ? (
                    // Grouped by BOL
                    filteredData.map((group, groupIndex) => (
                      <React.Fragment key={group.bolNumber || groupIndex}>
                        <tr className="bol-header">
                          <td colSpan="11">
                            <div className="bol-header-content">
                              <span className="bol-header-left">BOL#: {group.bolNumber}</span>
                              <span className="bol-header-center">Ship via: {group.shipVia}</span>
                              <span className="bol-header-right">Trailer: {group.trailerNo}</span>
                            </div>
                          </td>
                        </tr>
                        {group.releases && Array.isArray(group.releases) ? group.releases.map((release, releaseIndex) => (
                          <tr key={`${groupIndex}-${releaseIndex}`} className={`release-row ${releaseIndex % 2 === 0 ? 'row-even' : 'row-odd'}`}>
                            <td>{release.release}</td>
                            <td>{release.description}</td>
                            <td className="text-center">
                          {release.status ? loadStatusDisplayAsStored(release.status) : '—'}
                        </td>
                            <td className="text-center">{release.cli}</td>
                            <td className="text-center">{release.sac}</td>
                            <td className="text-center">{release.acct}</td>
                            <td className="text-center">{release.region}</td>
                            <td className="text-right">{release.shipWeight}</td>
                            <td className="text-right">{release.inTransit}</td>
                            <td className="text-right">{release.placedWeight}</td>
                            <td className="text-right">{release.placed}</td>
                          </tr>
                        )) : null}
                      </React.Fragment>
                    ))
                  ) : (
                    // Grouped by Line Item
                    filteredData
                      .filter((group) => group?.releases && Array.isArray(group.releases) && group.releases.length > 0)
                      .map((group, groupIndex) => (
                      <React.Fragment key={group.lineItem || groupIndex}>
                        <tr className="line-item-header">
                          <td colSpan="11">Line Item: {group.lineItem}</td>
                        </tr>
                        {group.releases && Array.isArray(group.releases) ? group.releases.map((release, releaseIndex) => (
                          <tr key={`${groupIndex}-${releaseIndex}`} className={`release-row ${releaseIndex % 2 === 0 ? 'row-even' : 'row-odd'}`}>
                            <td>{release.release}</td>
                            <td>{release.description}</td>
                            <td className="text-center">
                          {release.status ? loadStatusDisplayAsStored(release.status) : '—'}
                        </td>
                            <td className="text-center">{release.cli}</td>
                            <td className="text-center">{release.sac}</td>
                            <td className="text-center">{release.acct}</td>
                            <td className="text-center">{release.region}</td>
                            <td className="text-right">{release.shipWeight}</td>
                            <td className="text-right">{release.inTransit}</td>
                            <td className="text-right">{release.placedWeight}</td>
                            <td className="text-right">{release.placed}</td>
                          </tr>
                        )) : null}
                        {group.releases && Array.isArray(group.releases) && group.releases.length > 0 ? (
                          <tr className="line-item-total">
                            <td colSpan="6"></td>
                            <td className="text-start fw-bold line-item-total-border">Total for Line Item #{group.lineItem}:</td>
                            <td className="text-right fw-bold line-item-total-border">{group.totalShipWeight ? group.totalShipWeight.toFixed(0) : '0'}</td>
                            <td className="line-item-total-border"></td>
                            <td className="text-right fw-bold line-item-total-border">{group.totalPlacedWeight ? group.totalPlacedWeight.toFixed(0) : '0'}</td>
                            <td className="line-item-total-border"></td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Export Buttons */}
      <div className="report-footer">
        <div className="d-flex gap-3 justify-content-center">
          <Button variant="success" onClick={handleExportPDF}>
            <Download size={16} className="me-2" />
            Export PDF
          </Button>
          <Button variant="primary" onClick={handleExportExcel}>
            <FileSpreadsheet size={16} className="me-2" />
            Export Excel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseReport;

